import { z } from 'zod';
export { TRAVEL_PARTNER_CARI_CODES, travelCustomerCariCode } from '@polatkan/db/travel-cari-types';

export const travelCariSourceSchema = z.enum(['customer', 'partner']);
export type TravelCariSource = z.infer<typeof travelCariSourceSchema>;
export const travelCariPaymentTerms = ['PREPAID', 'CREDIT_15', 'CREDIT_30', 'CREDIT_60'] as const;
export const travelCariCurrencies = ['TRY', 'EUR', 'GBP', 'USD'] as const;

const text = z.string().trim().max(250, 'tooLong').default('');
const fields = {
  firstName: text,
  lastName: text,
  companyName: text,
  tradeName: text,
  contactName: text,
  email: z.string().trim().toLowerCase().email('email').or(z.literal('')).default(''),
  phone: text,
  secondaryPhone: text,
  taxId: text,
  registrationNo: text,
  addressLine1: text,
  city: text,
  country: text,
  website: z
    .string()
    .trim()
    .url('website')
    .or(z.literal(''))
    .refine((value) => !value || /^https?:\/\//i.test(value), 'website')
    .default(''),
  currency: z
    .string()
    .trim()
    .regex(/^[A-Z]{3}$/, 'currency')
    .default('TRY'),
  paymentTerms: z.enum(travelCariPaymentTerms).default('PREPAID'),
  commissionRate: z
    .number({ invalid_type_error: 'commissionRate' })
    .finite('commissionRate')
    .min(0, 'commissionRate')
    .max(100, 'commissionRate')
    .default(0),
  creditLimit: z
    .number({ invalid_type_error: 'creditLimit' })
    .finite('creditLimit')
    .min(0, 'creditLimit')
    .max(9999999999.99, 'creditLimit')
    .default(0),
  creditLimitEnabled: z.boolean().default(false),
  notes: z.string().trim().max(10000, 'tooLong').default(''),
};

/** Shared by the account editor and API, including the supported payment terms. */
export function travelCariFormSchema(source: TravelCariSource, organisation = false) {
  return z.object({
    ...fields,
    firstName:
      source === 'customer' && !organisation
        ? z.string().trim().min(1, 'firstName').max(250, 'tooLong')
        : text,
    companyName:
      source === 'partner' || organisation
        ? z.string().trim().min(1, 'companyName').max(250, 'tooLong')
        : text,
  });
}
export type TravelCariFormValues = z.infer<ReturnType<typeof travelCariFormSchema>>;

export const travelCariCreateSchema = z
  .object(fields)
  .extend({
    source: travelCariSourceSchema,
    cariTypeCode: z.string().trim().min(1).max(6),
  })
  .superRefine((value, ctx) => {
    const field =
      value.source === 'partner' || ['W', 'E'].includes(value.cariTypeCode)
        ? 'companyName'
        : 'firstName';
    if (!value[field]) ctx.addIssue({ code: 'custom', path: [field], message: field });
  });
export const travelCariUpdateSchema = z
  .object(fields)
  .partial()
  .extend({
    source: travelCariSourceSchema,
    id: z.string().min(1),
    // Optimistic concurrency: never silently overwrite another person's edits.
    expectedUpdatedAt: z.date(),
  })
  .superRefine((value, ctx) => {
    if (value.source === 'partner' && value.companyName === '')
      ctx.addIssue({ code: 'custom', path: ['companyName'], message: 'companyName' });
  });

export function normalizeTravelWebsite(value: string): string {
  const trimmed = value.trim();
  return trimmed && !/^[a-z][a-z\d+.-]*:/i.test(trimmed) ? `https://${trimmed}` : trimmed;
}

export function parseTravelAmount(value: string | number): number {
  if (typeof value === 'number') return value;
  return value.trim() ? Number(value.trim().replace(',', '.')) : 0;
}

export function travelCariHref(source: TravelCariSource, id: string): string {
  return `/travel/cari/accounts/${source}/${encodeURIComponent(id)}`;
}

export const travelCariStatementSchema = z
  .object({
    source: travelCariSourceSchema,
    id: z.string().min(1),
    fiscalYear: z.number().int().min(2000).max(2100),
    currency: z.enum(travelCariCurrencies).optional(),
    dateFrom: z.date().optional(),
    dateTo: z.date().optional(),
    page: z.number().int().min(1).default(1),
    pageSize: z.number().int().min(1).max(200).default(50),
  })
  .refine((value) => !value.dateFrom || !value.dateTo || value.dateFrom <= value.dateTo, {
    path: ['dateTo'],
    message: 'dateRange',
  });

export const travelCariContactSchema = z.object({
  name: z.string().trim().min(1, 'contactName').max(250, 'tooLong'),
  role: text,
  email: z.string().trim().toLowerCase().email('email').or(z.literal('')).default(''),
  phone: text,
  isPrimary: z.boolean().default(false),
});
